import { useEffect, useMemo, useState } from 'react';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { inr, Sheet } from '../design/atoms';
import CoachCard, { AnyCoachCard, CoachCardData } from './CoachCard';

/* "Choose your coach" — the checkout step for an offering that lets the
   member pick who trains them (any role: a PT, a therapist, a tennis coach).
   Prices are this offering's price with each coach (their level or own
   price); "Next free" comes live from the booking engine. */

interface CoachOption extends CoachCardData {
  price: number;
  gender: string | null;
}
interface CoachStepData {
  today: string;
  offering: { kind: string; id: string; name: string };
  required: boolean;
  allowAny: boolean;
  anyPrice: number;
  coaches: CoachOption[];
  facets: { specialties: string[]; languages: string[]; levels: Array<{ id: string; name: string }>; genders: string[] };
}

export interface CoachChoice { trainerId: string | null; trainerName: string | null; price: number }

const GENDER_LABEL: Record<string, string> = { female: 'Women', male: 'Men', non_binary: 'Non-binary' };

export default function CoachStep({ token, gymId, kind, id, current, onClose, onChoose }: {
  token: string;
  gymId: string;
  kind: 'plan' | 'pack';
  id: string;
  current?: CoachChoice | null;
  onClose: () => void;
  onChoose: (choice: CoachChoice) => void;
}) {
  const [data, setData] = useState<CoachStepData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<{ key: 'specialty' | 'level' | 'language' | 'gender'; value: string } | null>(null);
  const [picked, setPicked] = useState<string | null | undefined>(current ? current.trainerId : undefined);

  useEffect(() => {
    let live = true;
    mfetch(token, `/member/gyms/${gymId}/store/${kind}/${id}/coaches`)
      .then(r => { if (live) setData(r.data); })
      .catch(e => { if (live) setError(e instanceof MemberApiError ? e.message : 'Could not load coaches'); });
    return () => { live = false; };
  }, [token, gymId, kind, id]);

  const shown = useMemo(() => {
    if (!data) return [];
    if (!filter) return data.coaches;
    return data.coaches.filter(c =>
      filter.key === 'specialty' ? c.specialties.includes(filter.value)
      : filter.key === 'language' ? c.languages.includes(filter.value)
      : filter.key === 'gender' ? c.gender === filter.value
      : c.level?.name === filter.value);
  }, [data, filter]);

  const chips = data ? [
    ...data.facets.specialties.map(v => ({ key: 'specialty' as const, value: v, label: v })),
    ...data.facets.levels.map(l => ({ key: 'level' as const, value: l.name, label: l.name })),
    ...data.facets.languages.map(v => ({ key: 'language' as const, value: v, label: v })),
    ...data.facets.genders.map(v => ({ key: 'gender' as const, value: v, label: GENDER_LABEL[v] ?? v })),
  ] : [];

  const chosen = data && picked !== undefined
    ? picked === null
      ? { trainerId: null, trainerName: null, price: data.anyPrice }
      : (() => { const c = data.coaches.find(x => x.id === picked); return c ? { trainerId: c.id, trainerName: c.name, price: c.price } : null; })()
    : null;

  return (
    <Sheet onClose={onClose}>
      <div className="col" style={{ gap: 2 }}>
        <div className="dp dp-m">Choose your coach</div>
        <div className="sub trunc">{data?.offering.name ?? ' '}</div>
      </div>

      {error && <div className="card flat" style={{ padding: 12 }}><div className="sub wrap">{error}</div></div>}
      {!data && !error && <div className="skel" style={{ height: 420, borderRadius: 'var(--r-l)' }} />}

      {data && (
        <>
          {chips.length > 1 && (
            <div className="hscroll">
              <button className={`chip${filter ? '' : ' on'}`} onClick={() => setFilter(null)}>All</button>
              {chips.map(c => (
                <button key={`${c.key}:${c.value}`} className={`chip${filter?.key === c.key && filter.value === c.value ? ' on' : ''}`} onClick={() => setFilter(c)}>
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {data.coaches.length === 0 && !data.allowAny ? (
            <div className="card flat" style={{ padding: 16 }}>
              <div className="sub wrap">No coaches are taking new members for this right now — ask at the front desk.</div>
            </div>
          ) : (
            <div className="coach-rail">
              {data.allowAny && !filter && (
                <AnyCoachCard price={data.anyPrice} selected={picked === null} onSelect={() => setPicked(null)} />
              )}
              {shown.map(c => (
                <CoachCard
                  key={c.id}
                  coach={c}
                  today={data.today}
                  price={{ amount: c.price }}
                  selected={picked === c.id}
                  onSelect={() => setPicked(c.id)}
                />
              ))}
              {shown.length === 0 && filter && (
                <div className="card flat" style={{ padding: 16 }}><div className="sub wrap">No coach matches that filter.</div></div>
              )}
            </div>
          )}

          <button className="btn gold block" disabled={!chosen} onClick={() => chosen && onChoose(chosen)}>
            {chosen
              ? <>{chosen.trainerName ? `Continue with ${chosen.trainerName.split(' ')[0]}` : 'Continue with any coach'} · {inr(chosen.price)} <Icon n="right" s={17} /></>
              : data.required ? 'Pick a coach to continue' : 'Pick a coach, or any available'}
          </button>
        </>
      )}
    </Sheet>
  );
}
